import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useEffectivePermissions } from '../../auth/useEffectivePermissions'
import { Badge, Button, EmptyState, Modal, Segmented, Skeleton, TextInput, type SegmentedOption } from '../../shared/ui'
import { notify } from '../../store/notificationStore'
import { useSchultypOhne, useSetSchultyp, type SchultypOhneSchule } from './api/schultypApi'
import { SCHULTYPEN, STATUS_FILTER, schultypErrorKey, type Schultyp, type StatusFilter } from './schultyp'

type Pick = Schultyp | ''

// Four short labels fit one row at 390 px; the long label is the tooltip of each segment.
const OPTIONS: readonly SegmentedOption<Pick>[] = SCHULTYPEN.map(s => ({ value: s.value, label: s.kurz, title: s.lang }))

/** Unmount on close so reopening starts with a fresh search, page and no unsaved picks. */
export function SchultypDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  return open ? <SchultypDialogContent onClose={onClose} /> : null
}

function SchultypDialogContent({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation('schulen')
  const { canWrite } = useEffectivePermissions()
  const allowed = canWrite('schulen')
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState<{ seite: number; q: string; status: StatusFilter }>({ seite: 1, q: '', status: 'alle' })
  // Unsaved picks and row errors stay when paging or searching, keyed by the canonical BIGINT string.
  const [picks, setPicks] = useState<Record<string, Schultyp>>({})
  const [errors, setErrors] = useState<Record<string, string>>({})
  const list = useSchultypOhne(filter, allowed)
  const save = useSetSchultyp()
  const searching = search.trim() !== filter.q
  const busy = save.isPending
  const pages = Math.max(1, Math.ceil((list.data?.gesamt ?? 0) / (list.data?.proSeite ?? 25)))

  useEffect(() => {
    const timer = window.setTimeout(() => setFilter(current => ({ ...current, q: search.trim(), seite: 1 })), 250)
    return () => window.clearTimeout(timer)
  }, [search])

  // A saved row leaves the list; the last row of the last page can make the current page disappear.
  useEffect(() => {
    if (list.data && !list.isFetching && filter.seite > pages) setFilter(current => ({ ...current, seite: pages }))
  }, [list.data, list.isFetching, filter.seite, pages])

  const statusOptions: readonly SegmentedOption<StatusFilter>[] = STATUS_FILTER.map(value => ({ value, label: t(`schultyp.status.${value}`) }))
  const emptyKey = filter.status === 'aktiv' ? 'schultyp.emptyAktiv' : filter.status === 'inaktiv' ? 'schultyp.emptyInaktiv' : 'schultyp.empty'

  const pick = (school: SchultypOhneSchule, value: Pick) => {
    if (value === '') return
    setPicks(current => ({ ...current, [school.schoolId]: value }))
    setErrors(current => {
      const next = { ...current }
      delete next[school.schoolId]
      return next
    })
  }
  const submit = async (school: SchultypOhneSchule) => {
    const schultyp = picks[school.schoolId]
    if (!allowed || busy || !schultyp) return
    try {
      await save.mutateAsync({ schoolId: school.schoolId, schultyp })
      setPicks(current => {
        const next = { ...current }
        delete next[school.schoolId]
        return next
      })
      notify.success(t('schultyp.saved'), t('schultyp.savedDetail', { school: school.schulName, type: t(`schultyp.types.${schultyp}`) }))
    } catch (error) {
      setErrors(current => ({ ...current, [school.schoolId]: schultypErrorKey(error) }))
    }
  }

  if (!allowed) return null
  const unsaved = Object.keys(picks).length
  return (
    <Modal open onClose={onClose} blocked={busy} title={t('schultyp.title')} subtitle={t('schultyp.hint')} size="lg"
      footer={
        <div className="flex w-full flex-wrap items-center justify-between gap-3">
          <span className="text-sm text-on-surface-variant" aria-live="polite">
            {list.data && list.data.gesamt > 0 && t('schultyp.page', { page: list.data.seite, pages, count: list.data.gesamt })}
          </span>
          <div className="flex flex-wrap items-center gap-2">
            <Button icon="chevron_left" disabled={busy || searching || list.isFetching || filter.seite <= 1}
              onClick={() => setFilter(current => ({ ...current, seite: current.seite - 1 }))}>{t('schultyp.previous')}</Button>
            <Button icon="chevron_right" disabled={busy || searching || list.isFetching || !list.data || filter.seite >= pages}
              onClick={() => setFilter(current => ({ ...current, seite: current.seite + 1 }))}>{t('schultyp.next')}</Button>
            <Button variant="ghost" disabled={busy} onClick={onClose}>{t('schultyp.close')}</Button>
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        <TextInput value={search} onChange={event => setSearch(event.target.value)} maxLength={80} disabled={busy}
          leadingIcon="search" placeholder={t('schultyp.search')} aria-label={t('schultyp.search')} />
        <Segmented<StatusFilter> size="xs" className="w-fit max-w-full" value={filter.status} options={statusOptions} ariaLabel={t('schultyp.statusLabel')}
          onChange={status => setFilter(current => ({ ...current, status, seite: 1 }))} />
        {unsaved > 0 && <p className="text-sm text-on-surface-variant">{t('schultyp.unsaved', { count: unsaved })}</p>}
        {list.isError && (
          <div className="space-y-2" role="alert">
            <p className="text-sm text-error">{t(schultypErrorKey(list.error))}</p>
            <Button icon="refresh" loading={list.isFetching} onClick={() => void list.refetch()}>{t('schultyp.retry')}</Button>
          </div>
        )}
        {list.isLoading || searching ? <Skeleton className="h-48 w-full" label={t('schultyp.loading')} /> : !list.isError && list.data && (
          list.data.zeilen.length === 0
            ? (filter.q
              ? <EmptyState icon="search_off" title={t('schultyp.emptySearch')} description={t('schultyp.emptySearchHint')} />
              : <EmptyState icon="check_circle" title={t(emptyKey)} />)
            : <ul className="divide-y divide-outline-variant/30" aria-label={t('schultyp.schools')} aria-busy={list.isFetching}>
              {list.data.zeilen.map(school => {
                const chosen = picks[school.schoolId]
                const errorKey = errors[school.schoolId]
                const detail = [school.schulnummer, school.stadt].filter(Boolean).join(' · ')
                return (
                  <li key={school.schoolId} className="py-4 space-y-2">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="text-sm font-semibold text-on-surface break-words">{school.schulName}</h3>
                          {!school.aktiv && <Badge tone="neutral" size="sm" outlined>{t('schultyp.inactive')}</Badge>}
                        </div>
                        {detail && <p className="text-sm text-on-surface-variant">{detail}</p>}
                        <p className="text-xs text-on-surface-variant mt-1">
                          {t('schultyp.before', { value: school.aktuellerWert || t('schultyp.beforeNone') })}
                        </p>
                      </div>
                      <div className="flex flex-wrap items-center gap-3 sm:shrink-0">
                        <Segmented<Pick> choice size="xs" value={chosen ?? ''} options={OPTIONS} onChange={value => pick(school, value)}
                          ariaLabel={t('schultyp.pickFor', { school: school.schulName })} />
                        <Button variant="primary" icon="save" disabled={!chosen || busy || list.isFetching}
                          loading={busy && save.variables?.schoolId === school.schoolId}
                          aria-label={t('schultyp.saveFor', { school: school.schulName })} onClick={() => void submit(school)}>{t('schultyp.save')}</Button>
                      </div>
                    </div>
                    {errorKey && <p role="alert" className="text-sm text-error">{t(errorKey)}</p>}
                  </li>
                )
              })}
            </ul>
        )}
      </div>
    </Modal>
  )
}
